import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchDecks,
  fetchDeckWithCards,
  createDeck,
  updateDeck,
  deleteDeck,
  setActiveDeck,
} from '../api/decks';
import { Deck, DeckWithCards, CreateDeckRequest, UpdateDeckRequest } from '../types/decks';

export function useDecks() {
  return useQuery<Deck[]>({
    queryKey: ['decks'],
    queryFn: fetchDecks,
    staleTime: 1000 * 60 * 5, // 5분
  });
}

export function useDeck(deckId: string | null) {
  return useQuery<DeckWithCards | null>({
    queryKey: ['deck', deckId],
    queryFn: () => (deckId ? fetchDeckWithCards(deckId) : null),
    enabled: !!deckId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateDeck() {
  const queryClient = useQueryClient();

  return useMutation<Deck, Error, CreateDeckRequest>({
    mutationFn: createDeck,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['decks'] });
    },
  });
}

export function useUpdateDeck() {
  const queryClient = useQueryClient();

  return useMutation<Deck, Error, { deckId: string; updates: UpdateDeckRequest }>({
    mutationFn: ({ deckId, updates }) => updateDeck(deckId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['decks'] });
      queryClient.invalidateQueries({ queryKey: ['deck', variables.deckId] });
    },
  });
}

export function useDeleteDeck() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteDeck,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['decks'] });
    },
  });
}

export function useSetActiveDeck() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: setActiveDeck,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['decks'] });
    },
  });
}

